/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import { Close, Add } from '@mui/icons-material';
import { CustomTextField, ContainedButton } from '../../index';

const boxStyle = {
  mx: 0,
  my: 1,
  px: 3,
  py: 2,
};

export const AddressesTab = (props) => {
  const handleChange = (index, field, value) => {
    props.setEmpAddress((prev) => {
      let updated = [...prev];

      if (field === 'zipcode') {
        const parsed = value === '' ? undefined : Number(value);
        updated[index] = { ...updated[index], [field]: parsed };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }

      if (field === 'isresidential' || field === 'ispermanent') {
        updated[index] = { ...updated[index], [field]: value };

        // Prevent both from being false
        if (!updated[index].isresidential && !updated[index].ispermanent) {
          updated[index].ispermanent = true;
        }
      }

      // If the address is both permanent and residential, remove the other address
      if (updated[index].ispermanent && updated[index].isresidential) {
        updated = [updated[index]];
      }

      return updated;
    });
  };

  const handleAdd = () => {
    props.setEmpAddress((prev) => {
      // If no address yet
      if (prev.length === 0) {
        return [
          {
            empid: props.empid,
            isresidential: false,
            ispermanent: true,
            telephone: '',
            unitnumber: '',
            lotnumber: '',
            housebldgnum: '',
            street: '',
            barangay: '',
            subdivision: '',
            city: '',
            province: '',
            region: '',
            zipcode: undefined,
            country: '',
          },
        ];
      }

      const existing = prev[0];
      const newIsPermanent = !existing.ispermanent;
      const newIsResidential = !existing.isresidential;

      const newAddress = {
        empid: props.empid,
        isresidential: newIsResidential,
        ispermanent: newIsPermanent,
        telephone: '',
        unitnumber: '',
        lotnumber: '',
        housebldgnum: '',
        street: '',
        barangay: '',
        subdivision: '',
        city: '',
        province: '',
        region: '',
        zipcode: undefined,
        country: '',
      };

      return [newAddress, ...prev];
    });
  };

  const handleDelete = (index) => {
    props.setEmpAddress((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ ...boxStyle, maxHeight: '400px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {!props.viewOnly && (
          <ContainedButton
            startIcon={<Add />}
            onClick={handleAdd}
            variant="contained"
            btnTitle="Add Address"
            style={{ width: 'auto' }}
            disabled={
              props.empAddress.length >= 2 ||
              props.empAddress.some(
                (addr) => addr.ispermanent && addr.isresidential
              )
            }
          />
        )}
      </Box>
      {props.empAddress.map((address, index) => (
        <Box
          key={index}
          sx={{
            bgcolor: 'rgb(26, 32, 44, 0.6)',
            display: 'flex',
            justifyContent: 'start',
            p: 2,
            mt: 2,
            borderRadius: '5px',
            position: 'relative',
          }}
        >
          {!props.viewOnly && (
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(index)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                mr: 1,
              }}
            >
              <Close />
            </IconButton>
          )}
          <Grid container columnSpacing={2} sx={{ mt: 3 }}>
            <Grid item xs={4}>
              <CustomTextField
                errorMsg="Please enter a unit number"
                label="Unit Number"
                value={address.unitnumber || ''}
                onChange={(e) =>
                  handleChange(index, 'unitnumber', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={4}>
              <CustomTextField
                errorMsg="Please enter a lot number"
                label="Lot Number"
                value={address.lotnumber || ''}
                onChange={(e) =>
                  handleChange(index, 'lotnumber', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={4}>
              <CustomTextField
                errorMsg="Please enter a house/building Number"
                label="House/Building Number"
                value={address.housebldgnum || ''}
                onChange={(e) =>
                  handleChange(index, 'housebldgnum', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!address.street}
                errorMsg="Please enter a street"
                label="Street"
                value={address.street || ''}
                onChange={(e) => handleChange(index, 'street', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!address.barangay}
                errorMsg="Please enter a barangay"
                label="Barangay"
                value={address.barangay || ''}
                onChange={(e) =>
                  handleChange(index, 'barangay', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a subdivision"
                label="Subdivision"
                value={address.subdivision || ''}
                onChange={(e) =>
                  handleChange(index, 'subdivision', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomTextField
                error={!address.city}
                errorMsg="Please enter a city"
                label="City"
                value={address.city || ''}
                onChange={(e) => handleChange(index, 'city', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={3}>
              <CustomTextField
                error={!address.province}
                errorMsg="Please enter a province"
                label="Province"
                value={address.province || ''}
                onChange={(e) =>
                  handleChange(index, 'province', e.target.value)
                }
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!address.region}
                errorMsg="Please enter a region"
                label="Region"
                value={address.region || ''}
                onChange={(e) => handleChange(index, 'region', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!address.zipcode}
                type="number"
                errorMsg="Please enter a ZIP code"
                label="ZIP Code"
                value={address.zipcode || ''}
                onChange={(e) => handleChange(index, 'zipcode', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                error={!address.country}
                errorMsg="Please enter a country"
                label="Country"
                value={address.country || ''}
                onChange={(e) => handleChange(index, 'country', e.target.value)}
                viewOnly={props.viewOnly}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextField
                errorMsg="Please enter a telephone no."
                label="Telephone No."
                value={address.telephone || ''}
                onChange={(e) =>
                  handleChange(index, 'telephone', e.target.value)
                }
                viewOnly={props.viewOnly}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={address.isresidential}
                    onChange={
                      !props.viewOnly
                        ? (e) => {
                            const checked = e.target.checked;
                            handleChange(index, 'isresidential', checked);
                          }
                        : undefined
                    }
                  />
                }
                label="Residential Address"
              />
            </Grid>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={address.ispermanent}
                    onChange={
                      !props.viewOnly
                        ? (e) => {
                            const checked = e.target.checked;
                            handleChange(index, 'ispermanent', checked);
                          }
                        : undefined // read-only in viewOnly mode
                    }
                  />
                }
                label="Permanent Address"
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
